import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import "./GiftSessionWizard.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const getEffectiveMetadata = (product) => ({
  ...(product?.metadata || {}),
  ...(product?.price?.metadata || {}),
});

const normalizeValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

const isSharedPackageProduct = (product) => {
  const metadata = getEffectiveMetadata(product);
  return (
    normalizeValue(metadata.creditModel) === "unified-shared" &&
    (normalizeValue(metadata.creditCategory) === "package" ||
      normalizeValue(metadata.productType) === "package")
  );
};

const isSharedDropInProduct = (product) => {
  const metadata = getEffectiveMetadata(product);
  const productType = normalizeValue(metadata.productType);
  const creditCategory = normalizeValue(metadata.creditCategory);

  return (
    normalizeValue(metadata.creditModel) === "unified-shared" &&
    (creditCategory === "drop-in" ||
      creditCategory === "dropin" ||
      productType === "drop-in" ||
      productType === "dropin" ||
      productType === "single-session")
  );
};

const getGiftCategoryLabel = (giftType) =>
  giftType === "package" ? "Package" : "Drop-In Session";

const GiftSessionWizard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [giftType, setGiftType] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sessionCount, setSessionCount] = useState(1);
  const [recipient, setRecipient] = useState({ name: "", email: "" });
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [accountCreationConsent, setAccountCreationConsent] = useState(false);
  const [noRefundConsent, setNoRefundConsent] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/products`
        );

        const filteredProducts = response.data.filter((product) => {
          const metadata = getEffectiveMetadata(product);
          return (
            normalizeValue(metadata.isIntroOffer) !== "true" &&
            (isSharedPackageProduct(product) || isSharedDropInProduct(product))
          );
        });

        setProducts(filteredProducts);
      } catch (fetchError) {
        setError("Failed to load gift products. Please try again later.");
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    if (giftType === "package") {
      return products.filter((product) => isSharedPackageProduct(product));
    }

    if (giftType === "drop-in") {
      return products.filter((product) => isSharedDropInProduct(product));
    }

    return [];
  }, [giftType, products]);

  const nextStep = () => {
    if (validateCurrentStep()) {
      setStep((current) => current + 1);
      setError("");
    }
  };

  const prevStep = () => {
    setStep((current) => current - 1);
    setError("");
  };

  const validateCurrentStep = () => {
    switch (step) {
      case 1:
        if (!giftType) {
          setError("Please select a gift type");
          return false;
        }
        return true;
      case 2:
        if (!selectedProduct) {
          setError("Please select a product");
          return false;
        }
        return true;
      case 3: {
        if (!recipient.email) {
          setError("Recipient email is required");
          return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(recipient.email)) {
          setError(`Invalid email format: ${recipient.email}`);
          return false;
        }

        if (
          user &&
          user.email &&
          recipient.email.toLowerCase() === user.email.toLowerCase()
        ) {
          setError("You cannot gift to yourself");
          return false;
        }
        return true;
      }
      case 5:
        if (!accountCreationConsent) {
          setError(
            "You must acknowledge that an account will be created for the recipient"
          );
          return false;
        }
        if (!noRefundConsent) {
          setError("You must acknowledge the no-refund policy");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const updateRecipient = (field, value) => {
    setRecipient((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    const metadata = getEffectiveMetadata(product);

    if (giftType === "package") {
      setSessionCount(
        parseInt(metadata.totalSessions || metadata.sessionCredits, 10) || 1
      );
      return;
    }

    setSessionCount(1);
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) {
      return;
    }

    if (!token) {
      navigate("/login", { state: { from: "/gift-sessions" } });
      return;
    }

    setLoading(true);
    setError("");

    try {
      const csrfResponse = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/csrf-token`,
        {
          withCredentials: true,
        }
      );

      const csrfToken = csrfResponse.data.csrfToken;

      if (!csrfToken) {
        throw new Error(
          "Failed to obtain CSRF token. Please refresh the page and try again."
        );
      }

      const checkoutData = {
        productId: selectedProduct.id,
        recipient,
        note,
        sessionCount,
        giftType: giftType === "package" ? "shared-package" : "shared-drop-in",
        _csrf: csrfToken,
      };

      const requestHeaders = {
        Authorization: `Bearer ${token}`,
        "X-XSRF-TOKEN": csrfToken,
      };

      const checkoutResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/gifts/checkout`,
        checkoutData,
        {
          headers: requestHeaders,
          withCredentials: true,
        }
      );

      const stripe = await stripePromise;
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: checkoutResponse.data.sessionId,
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }
    } catch (submitError) {
      let errorMessage = "Failed to create gift. Please try again.";

      if (submitError.response) {
        if (
          submitError.response.status === 403 &&
          submitError.response.data.error === "CSRF_ERROR"
        ) {
          errorMessage =
            "Security validation failed. Please refresh the page and try again.";
        } else {
          errorMessage = submitError.response.data.message || errorMessage;
        }
      } else if (submitError.message) {
        errorMessage = submitError.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="gift-step">
      <h2>Select Gift Type</h2>
      <div className="gift-options">
        <div
          className={`gift-option ${giftType === "package" ? "selected" : ""}`}
          onClick={() => {
            setGiftType("package");
            setSelectedProduct(null);
          }}
        >
          <h3>Gift a Package</h3>
          <p>Gift shared flexible session credits in a package bundle.</p>
        </div>
        <div
          className={`gift-option ${giftType === "drop-in" ? "selected" : ""}`}
          onClick={() => {
            setGiftType("drop-in");
            setSelectedProduct(null);
          }}
        >
          <h3>Gift Drop-In Sessions</h3>
          <p>Gift single shared session credits usable for sauna or red light.</p>
        </div>
      </div>
      <div className="gift-step-buttons">
        <button className="next-button" onClick={nextStep}>
          Next
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="gift-step">
      <h2>Select a Product</h2>
      <p className="gift-step-description">
        Choose the shared {giftType === "package" ? "package" : "drop-in"} gift
        you'd like to send.
      </p>

      {filteredProducts.length === 0 ? (
        <div className="no-products-message">
          <p>No shared gift products are available for this category yet.</p>
          <p>Please check your Stripe catalog or try again later.</p>
        </div>
      ) : (
        <div className="product-options">
          {filteredProducts.map((product) => {
            const metadata = getEffectiveMetadata(product);
            return (
              <div
                key={product.id}
                className={`product-option ${
                  selectedProduct?.id === product.id ? "selected" : ""
                }`}
                onClick={() => handleProductSelect(product)}
              >
                {product.images?.length > 0 && (
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="package-card-image"
                  />
                )}
                <h3>{product.name}</h3>
                <p>{product.description}</p>
                <div className="product-price">
                  {product.price ? (
                    <span>${(product.price.unit_amount / 100).toFixed(2)}</span>
                  ) : (
                    <span>Price not available</span>
                  )}
                </div>
                {(metadata.totalSessions || metadata.sessionCredits) && (
                  <div className="product-sessions">
                    <span>
                      {metadata.totalSessions || metadata.sessionCredits} sessions
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="gift-step-buttons">
        <button className="back-button" onClick={prevStep}>
          Back
        </button>
        <button
          className="next-button"
          onClick={nextStep}
          disabled={filteredProducts.length === 0}
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="gift-step">
      <h2>Add Recipient</h2>
      <p className="gift-step-description">Enter the recipient's information</p>

      <div className="recipient-form">
        <div className="recipient-inputs">
          <input
            type="text"
            placeholder="Recipient Name (optional)"
            value={recipient.name}
            onChange={(e) => updateRecipient("name", e.target.value)}
          />
          <input
            type="email"
            placeholder="Recipient Email *"
            value={recipient.email}
            onChange={(e) => updateRecipient("email", e.target.value)}
            required
          />
        </div>
      </div>

      <div className="gift-step-buttons">
        <button className="back-button" onClick={prevStep}>
          Back
        </button>
        <button className="next-button" onClick={nextStep}>
          Next
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="gift-step">
      <h2>Personalize Your Gift</h2>

      {giftType === "drop-in" && (
        <div className="session-count-container">
          <label htmlFor="session-count">Number of Sessions:</label>
          <select
            id="session-count"
            value={sessionCount}
            onChange={(e) => setSessionCount(Number(e.target.value))}
          >
            {[1, 2, 3, 4, 5, 10].map((count) => (
              <option key={count} value={count}>
                {count}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="gift-note-container">
        <label htmlFor="gift-note">Add a Personal Note (Optional):</label>
        <textarea
          id="gift-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Enter your personal message here..."
          maxLength={200}
        />
        <div className="note-character-count">{note.length}/200 characters</div>
      </div>

      <div className="gift-step-buttons">
        <button className="back-button" onClick={prevStep}>
          Back
        </button>
        <button className="next-button" onClick={nextStep}>
          Next
        </button>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="gift-step">
      <h2>Review Your Gift</h2>

      <div className="gift-summary">
        <div className="summary-item">
          <span className="summary-label">Gift Type:</span>
          <span className="summary-value">{getGiftCategoryLabel(giftType)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Product:</span>
          <span className="summary-value">
            {selectedProduct?.name || "Selected gift"}
          </span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Number of Sessions:</span>
          <span className="summary-value">{sessionCount}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Recipient:</span>
          <span className="summary-value">
            {recipient.name
              ? `${recipient.name} (${recipient.email})`
              : recipient.email}
          </span>
        </div>
        {note && (
          <div className="summary-item">
            <span className="summary-label">Personal Note:</span>
            <p className="summary-note">{note}</p>
          </div>
        )}
      </div>

      <div className="consent-checkboxes">
        <div className="consent-item">
          <input
            type="checkbox"
            id="account-creation-consent"
            checked={accountCreationConsent}
            onChange={(e) => setAccountCreationConsent(e.target.checked)}
          />
          <label htmlFor="account-creation-consent">
            I understand an account will be created for the recipient if they
            don't already have one
          </label>
        </div>
        <div className="consent-item">
          <input
            type="checkbox"
            id="no-refund-consent"
            checked={noRefundConsent}
            onChange={(e) => setNoRefundConsent(e.target.checked)}
          />
          <label htmlFor="no-refund-consent">
            I understand there are no refunds for gift purchases
          </label>
        </div>
      </div>

      <div className="gift-step-buttons">
        <button className="back-button" onClick={prevStep}>
          Back
        </button>
        <button
          className="submit-button"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Processing..." : "Purchase Gift"}
        </button>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      default:
        return renderStep1();
    }
  };

  return (
    <div className="gift-session-wizard">
      <div className="wizard-progress">
        <div className={`progress-step ${step >= 1 ? "active" : ""}`}>1</div>
        <div className={`progress-line ${step >= 2 ? "active" : ""}`}></div>
        <div className={`progress-step ${step >= 2 ? "active" : ""}`}>2</div>
        <div className={`progress-line ${step >= 3 ? "active" : ""}`}></div>
        <div className={`progress-step ${step >= 3 ? "active" : ""}`}>3</div>
        <div className={`progress-line ${step >= 4 ? "active" : ""}`}></div>
        <div className={`progress-step ${step >= 4 ? "active" : ""}`}>4</div>
        <div className={`progress-line ${step >= 5 ? "active" : ""}`}></div>
        <div className={`progress-step ${step >= 5 ? "active" : ""}`}>5</div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {renderStep()}
    </div>
  );
};

export default GiftSessionWizard;
